import {
    Component,
    ElementRef,
    HostListener,
    AfterViewInit,
    Renderer2,
    OnInit,
    computed,
    input,
    viewChild,
} from '@angular/core';
import { themeState } from '@app/core/stores/themeStore';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-resizable',
    templateUrl: './resizable.component.html',
    styleUrls: ['./resizable.component.scss'],
    imports: [CommonModule],
})
export class ResizableComponent implements AfterViewInit, OnInit {
    splitPane = viewChild<ElementRef>('splitPane');
    leftPane = viewChild<ElementRef>('leftPane');
    rightPane = viewChild<ElementRef>('rightPane');
    divider = viewChild<ElementRef>('divider');

    initialLeftWidth = input(50); // Default to 50%
    initialRightWidth = input(50); // Default to 50%

    private normalizedLeftWidth = 50;
    private normalizedRightWidth = 50;

    private isDragging = false;
    private startX: number;
    private startLeftWidth: number;
    private screenMode: 'mobile' | 'tablet' | 'desktop';

    isPdfViewer = false;
    leftSideHidden = computed(() => themeState().leftSideHidden);

    constructor(private renderer: Renderer2) {}

    ngOnInit() {
        this.checkScreenMode();
    }

    ngAfterViewInit() {
        this.validateAndSetInitialWidths();
        this.setInitialWidths();
    }

    @HostListener('window:resize')
    onResize() {
        this.checkScreenMode();
        this.setInitialWidths();
    }

    setPdfViewerMode(isPdf: boolean) {
        this.isPdfViewer = isPdf;
    }

    private checkScreenMode() {
        const width = window.innerWidth;
        if (width < 640) {
            this.screenMode = 'mobile';
        } else if (width < 1024) {
            this.screenMode = 'tablet';
        } else {
            this.screenMode = 'desktop';
        }
    }

    private validateAndSetInitialWidths() {
        const totalWidth = this.initialLeftWidth() + this.initialRightWidth();
        if (totalWidth !== 100) {
            console.warn('Initial widths do not sum to 100%. Normalizing...');
            this.normalizedLeftWidth =
                (this.initialLeftWidth() / totalWidth) * 100;
            this.normalizedRightWidth =
                (this.initialRightWidth() / totalWidth) * 100;
        } else {
            this.normalizedLeftWidth = this.initialLeftWidth();
            this.normalizedRightWidth = this.initialRightWidth();
        }
    }

    private setInitialWidths() {
        if (this.screenMode === 'mobile') {
            this.renderer.setStyle(
                this.leftPane()?.nativeElement,
                'width',
                '100%'
            );
            this.renderer.setStyle(
                this.rightPane()?.nativeElement,
                'width',
                '100%'
            );
        } else {
            this.renderer.setStyle(
                this.leftPane()?.nativeElement,
                'width',
                `${this.normalizedLeftWidth}%`
            );
            this.renderer.setStyle(
                this.rightPane()?.nativeElement,
                'width',
                `${this.normalizedRightWidth}%`
            );
        }
    }

    @HostListener('window:mousemove', ['$event'])
    @HostListener('window:touchmove', ['$event'])
    onMove(event: MouseEvent | TouchEvent) {
        if (!this.isDragging || this.screenMode === 'mobile') return;

        const clientX =
            event instanceof MouseEvent
                ? event.clientX
                : event.touches[0].clientX;
        const dx = clientX - this.startX;
        const newLeftWidth = this.startLeftWidth + dx;
        const containerWidth = this.splitPane()?.nativeElement.offsetWidth;

        if (newLeftWidth > 100 && newLeftWidth < containerWidth - 100) {
            const leftPercentage = (newLeftWidth / containerWidth) * 100;
            const rightPercentage = 100 - leftPercentage;
            this.renderer.setStyle(
                this.leftPane()?.nativeElement,
                'width',
                `${leftPercentage}%`
            );
            this.renderer.setStyle(
                this.rightPane()?.nativeElement,
                'width',
                `${rightPercentage}%`
            );
        }
    }

    @HostListener('window:mouseup')
    @HostListener('window:touchend')
    onEnd() {
        this.isDragging = false;
    }

    onStart(event: MouseEvent | TouchEvent) {
        if (this.screenMode === 'mobile') return;
        this.isDragging = true;
        this.startX =
            event instanceof MouseEvent
                ? event.clientX
                : event.touches[0].clientX;
        this.startLeftWidth = this.leftPane()?.nativeElement.offsetWidth;
    }
}
